import { Heading, Link, Spinner, ProgressBar } from '@mm/components';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { ContactsCsvImportJobResponse } from '@mm/state/controllers/ContactsCotroller';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as CSVIcon } from 'assets/icons/csv-file.svg';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import Checkbox from '../../../../../common/components/Checkbox';
import {
  CSVDropParser,
  ParsedData,
} from '../../../../../common/modals/ImportContantListModal/CSVDropParser/CSVDropParser';
import {
  csvExample,
  CSVMapper,
  useDefaultImportContactList,
} from '../../../../../common/modals/ImportContantListModal/ImportContantListModal';
import { Routes } from '../../../../../routes/top-level-routes';
import { useInputTriggerCRUD } from '../../../../features/beta/modules/Automations/AutomationEdit/InputTriggers/use-input-trigger-crud';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './UploadContactListSlideoutEditor.module.css';
import { IndexAttributeNameMap } from '@mm/state/modules/modalsState/props';
import classNames from 'classnames';

export type UploadContactListSlideoutEditorProps = {
  campaignId: number;
};

export const UploadContactListSlideoutEditor = ({
  campaignId,
}: UploadContactListSlideoutEditorProps) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const botId = useSelector(activeChatbotIdSelector);
  const { importContactList, isLoading } = useDefaultImportContactList();
  const isSysOrCSAdmin = useSelector(selectCurrentUserIsAdmin);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const { getContactsCsvImportJob } = useContactsCRUDActions();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const modalActions = useModalActions();
  const viewActions = useViewActions();
  const { setRef } = useSlideoutOnClose();
  const inputTriggerCreator = useInputTriggerCRUD();
  const campaign = useSelector((state: StoreState) =>
    selectCampaignById(state, Number(campaignId)),
  )!;
  const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);
  const [isLoadingAudience, setIsLoadingAudience] = useState(false);
  const [file, setFile] = useState<File>();
  const [uploadContactsProgress, setUploadContactsProgress] = useState(0);

  const [fileData, setFileData] = useState<{
    csvFile?: File;
    parsedData?: ParsedData;
  }>({});
  const [mapping, setMapping] = useState<{
    isValid: boolean;
    map?: IndexAttributeNameMap;
  }>({
    isValid: false,
  });

  const onCsvFileParseComplete = (csvFile: File, parsedData: ParsedData) =>
    setFileData({ csvFile, parsedData });

  const onActiveButtonClick = async () => {
    const formattedMap = mapping.map;

    if (fileData.csvFile && formattedMap) {
      setIsLoadingAudience(true);
      const resp = await importContactList({
        facebookPageId,
        file: fileData.csvFile,
        mapping: formattedMap,
        isFirstRowHeader: true,
      });
      if (resp.id) {
        setUploadContactsProgress(20);
        const respJob = await new Promise<ContactsCsvImportJobResponse>(
          resolve => {
            const startInterval = () =>
              setTimeout(async () => {
                const response = await getContactsCsvImportJob(resp.id);
                setUploadContactsProgress(
                  uploadContactsProgress => uploadContactsProgress + 20,
                );
                response?.contactsCsv?.progressbar?.audienceId
                  ? resolve(response)
                  : startInterval();
              }, 5000);
            startInterval();
          },
        );
        const audienceId = respJob?.contactsCsv?.progressbar?.audienceId;
        if (audienceId) {
          setUploadContactsProgress(100);
          const addEmailPlatform =
            Object.values(formattedMap).includes('EMAIL');
          await inputTriggerCreator.uploadContactListTrigger({
            campaign,
            audienceId,
            addEmailPlatform,
          });
          audiencesCRUDActions.retrieveAll(botId);
          setIsLoadingAudience(false);
          viewActions.hideSlideoutMenu();
        }
      }
    }
  };

  const onCsvFileRemove = () => {
    setFileData({ csvFile: undefined, parsedData: undefined });
    setMapping({ isValid: false, map: undefined });
    setFile(undefined);
  };

  const onFileReject = () => {
    modalActions.openPrettyErrorModal({
      title: 'Import csv failed',
      errorMessage: `Please select csv file ${
        isSysOrCSAdmin
          ? '(10MB max)'
          : facebookPage?.free
          ? '(2MB max)'
          : '(5MB max)'
      }`,
    });
  };

  const onCsvMapUpdate = (map: IndexAttributeNameMap, isValid: boolean) =>
    setMapping({ map, isValid });

  const onAgreementToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreementAccepted(e.target.checked);
  };

  const isActiveButtonDisabled =
    !mapping.isValid || isLoading || !isAgreementAccepted || isLoadingAudience;

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <Spinner
        show={isLoadingAudience}
        color="#A8A8A8"
        width={'100%'}
        position="absolute"
      />
      <SlideoutMenu.Header className={styles.header} closeIcon="close">
        <Heading size={3}>Upload CSV file</Heading>
        <div className={styles.buttonsContainer}>
          <PrimaryButton
            className={styles.activateBtn}
            disabled={isActiveButtonDisabled}
            onClick={onActiveButtonClick}
          >
            Save
          </PrimaryButton>
        </div>
      </SlideoutMenu.Header>
      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.configArea}>
          {isLoadingAudience && (
            <div className={styles.progressBarContainer}>
              <ProgressBar
                progress={uploadContactsProgress / 100}
                caption="Uploading..."
              />
            </div>
          )}
          {!file && (
            <p className={styles.title}>
              Please upload the CSV file with the list of contacts in{' '}
              <Link
                href={csvExample}
                download="phonenumbers.csv"
                isBlank
                capitalize={false}
              >
                this format
              </Link>
              .
            </p>
          )}
          <CSVDropParser
            customFile={file}
            customSetFile={setFile}
            onSuccess={onCsvFileParseComplete}
            onFileRemove={onCsvFileRemove}
            onFileReject={onFileReject}
            isFirstRowHeader
            hideFirstRowDescription
            showUploadBtn
            showCheckIcon
            customText="Drop your file(s) here CSV only or"
            className={classNames(styles.csvDropParser, {
              [styles.csvFileUploaded]: file,
            })}
            customCSVIcon={
              <div
                className={classNames(styles.csvIcon, {
                  [styles.uploadedFileCsvIcon]: file,
                })}
              >
                <CSVIcon />
              </div>
            }
            showCsvIcon
          />
          {fileData.parsedData && (
            <>
              <CSVMapper
                columns={fileData.parsedData.meta.fields}
                onUpdate={onCsvMapUpdate}
                customDescriptiontext="Update field or create new field with attribute values:"
                hideColumnName
                customDropdownAlign="end"
              />
              <Checkbox
                className="import-contact-list-modal__checkbox"
                label={
                  <span>
                    I agree to the{' '}
                    <Link
                      className="import-contact-list-modal__link"
                      isBlank
                      href={Routes.masterServiceAgreement.getLink()}
                    >
                      Terms of Service
                    </Link>
                    , which requires that all of my contacts have opted in to
                    receive messages from me
                  </span>
                }
                isChecked={isAgreementAccepted}
                onToggle={onAgreementToggle}
              />
            </>
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
